import React, { useState, useRef, useEffect, useId } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import {
  Code,
  MoreVert,
  Image as ImageIcon,
  Visibility,
  Edit,
} from '@mui/icons-material';
import MarkdownRenderer from './MarkdownRenderer';
import axios from '../../api/axios';

// 按类别组织符号
const symbolCategories = [
  {
    name: '比较运算符',
    symbols: [
  { label: '不等于', symbol: '≠' },
  { label: '约等于', symbol: '≈' },
  { label: '小于等于', symbol: '≤' },
  { label: '大于等于', symbol: '≥' },
    ],
  },
  {
    name: '数学符号',
    symbols: [
  { label: '加减', symbol: '±' },
  { label: '无穷', symbol: '∞' },
  { label: '求和', symbol: '∑' },
  { label: '乘积', symbol: '∏' },
  { label: '积分', symbol: '∫' },
  { label: '根号', symbol: '√' },
    ],
  },
  {
    name: '希腊字母',
    symbols: [
  { label: 'Alpha', symbol: 'α' },
  { label: 'Beta', symbol: 'β' },
  { label: 'Gamma', symbol: 'γ' },
  { label: 'Pi', symbol: 'π' },
  { label: 'Lambda', symbol: 'λ' },
    ],
  },
  {
    name: '箭头',
    symbols: [
  { label: '箭头右', symbol: '→' },
  { label: '箭头左', symbol: '←' },
    ],
  },
  {
    name: '数集',
    symbols: [
      { label: '实数集 R', symbol: 'ℝ' },
      { label: '整数集 Z', symbol: 'ℤ' },
      { label: '有理数集 Q', symbol: 'ℚ' },
      { label: '自然数集 N', symbol: 'ℕ' },
    ],
  },
  {
    name: '逻辑与集合',
    symbols: [
      { label: '全称量词', symbol: '∀' },
      { label: '存在量词', symbol: '∃' },
      { label: '属于', symbol: '∈' },
      { label: '空集', symbol: '∅' },
      { label: '子集', symbol: '⊆' },
      { label: '交集', symbol: '∩' },
      { label: '并集', symbol: '∪' },
      { label: '逻辑与', symbol: '∧' },
      { label: '逻辑或', symbol: '∨' },
    ],
  },
];

// 扁平化所有符号（用于 symbolOnly 模式直接展示）
const allSymbols = symbolCategories.flatMap((c) => c.symbols);

/**
 * 统一的Markdown编辑器组件
 * 支持：
 * - Markdown输入
 * - 实时预览
 * - 图片上传
 * - 工具栏（插入代码、符号等）
 * 
 * @param {string} value - 当前内容值
 * @param {function} onChange - 内容变化回调
 * @param {string} label - 输入框标签
 * @param {number} minRows - 最小行数
 * @param {number} maxRows - 最大行数
 * @param {boolean} showPreview - 是否显示预览标签页
 * @param {boolean} showToolbar - 是否显示工具栏（兼容旧用法）
 * @param {string} toolbarMode - 工具栏模式：'full' 完整工具栏 | 'symbolOnly' 仅插入符号按钮 | 'none' 不显示
 * @param {boolean} symbolPanelOpen - 符号面板是否展开（受控模式，与 onSymbolPanelOpenChange 配合）
 * @param {function} onSymbolPanelOpenChange - 符号面板展开状态变化回调
 * @param {string} placeholder - 占位符文本
 * @param {boolean} disabled - 是否禁用
 * @param {boolean} disableImageUpload - 是否禁用图片上传功能
 * @param {boolean} fillHeight - 是否填满父容器高度；为 true 时仅内部输入/预览区域可滚动，工具栏与标签固定
 * @param {function} onKeyDown - 键盘按下回调（如用于 Enter 发送）
 */
function MarkdownEditor({
  value = '',
  onChange,
  onKeyDown,
  label = '内容',
  minRows = 6,
  maxRows = 20,
  showPreview = true,
  showToolbar = true,
  toolbarMode, // 'full' | 'symbolOnly' | 'none'，优先级高于 showToolbar
  symbolPanelOpen: symbolPanelOpenProp,
  onSymbolPanelOpenChange,
  placeholder = '输入Markdown格式的内容...',
  disabled = false,
  disableImageUpload = false,
  fillHeight = false,
}) {
  const [content, setContent] = useState(value);
  const [activeTab, setActiveTab] = useState(0); // 0: 编辑, 1: 预览
  const [symbolPanelOpenInternal, setSymbolPanelOpenInternal] = useState(false);
  const isSymbolPanelControlled = symbolPanelOpenProp !== undefined;
  const symbolPanelOpen = isSymbolPanelControlled ? symbolPanelOpenProp : symbolPanelOpenInternal;
  const setSymbolPanelOpen = (v) => {
    const next = typeof v === 'function' ? v(symbolPanelOpen) : v;
    if (!isSymbolPanelControlled) setSymbolPanelOpenInternal(next);
    onSymbolPanelOpenChange?.(next);
  };
  const [symbolCategoryTab, setSymbolCategoryTab] = useState(0);
  const [uploadingImages, setUploadingImages] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const contentInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadInputId = useId().replace(/:/g, '') || `upload-${Math.random().toString(36).slice(2, 9)}`;

  // 当符号面板展开时，动态调整 minRows
  const adjustedMinRows = symbolPanelOpen ? Math.max(4, minRows - 4) : minRows;
  const adjustedMaxRows = symbolPanelOpen ? Math.max(8, maxRows - 4) : maxRows;

  // 同步外部value变化
  useEffect(() => {
    setContent(value);
  }, [value]);

  // 插入文本到光标位置
  const insertTextAtCursor = (text) => {
    const textarea = contentInputRef.current;
    if (!textarea) {
      // 如果没有光标位置，追加到末尾
      const newContent = content + text;
      setContent(newContent);
      if (onChange) onChange(newContent);
      return;
    }

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentText = content || '';
    const newText = currentText.substring(0, start) + text + currentText.substring(end);
    
    setContent(newText);
    if (onChange) onChange(newText);
    
    // 恢复光标位置
    setTimeout(() => {
      const updatedTextarea = contentInputRef.current;
      if (updatedTextarea) {
        updatedTextarea.focus();
        const newPosition = start + text.length;
        updatedTextarea.setSelectionRange(newPosition, newPosition);
      }
    }, 50);
  };

  // 插入代码块
  const handleInsertCode = () => {
    insertTextAtCursor('\n```python\n# 在这里输入代码\n```\n');
  };

  // 插入符号
  const handleInsertSymbol = (symbol) => {
    insertTextAtCursor(symbol);
    // 插入后不关闭面板，方便连续插入多个符号
  };

  // 处理文件选择
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadError(null);
    const newUploadingImages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        setUploadError(`文件 ${file.name} 不是图片格式`);
        continue;
      }

      // 验证文件大小（5MB限制）
      if (file.size > 5 * 1024 * 1024) {
        setUploadError(`文件 ${file.name} 超过5MB限制`);
        continue;
      }

      const imageId = `uploading-${Date.now()}-${i}`;
      newUploadingImages.push({
        id: imageId,
        file,
        name: file.name,
        uploading: true,
        url: null,
        error: null,
      });

      // 上传图片
      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await axios.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            const image = newUploadingImages.find(img => img.id === imageId);
            if (image) {
              image.progress = percentCompleted;
            }
          },
        });

        // 上传成功，插入Markdown图片链接
        // API返回的URL是相对于/api的路径，需要构建完整URL
        let imageUrl = response.data.url || response.data.path;
        
        // 如果URL不是完整URL（不以http://或https://开头），构建完整URL
        if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
          // 如果URL以/api开头，直接使用（相对路径）
          // 否则，使用axios的baseURL构建完整URL
          if (!imageUrl.startsWith('/api')) {
            const baseURL = axios.defaults.baseURL || '';
            imageUrl = baseURL.replace('/api', '') + imageUrl;
          }
        }
        
        const markdownImage = `![${file.name}](${imageUrl})\n`;
        insertTextAtCursor(markdownImage);

        // 更新上传状态
        const image = newUploadingImages.find(img => img.id === imageId);
        if (image) {
          image.uploading = false;
          image.url = imageUrl;
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        const image = newUploadingImages.find(img => img.id === imageId);
        if (image) {
          image.uploading = false;
          image.error = error.response?.data?.error || '上传失败，请重试';
        }
        setUploadError(`图片 ${file.name} 上传失败: ${error.response?.data?.error || error.message}`);
      }
    }

    setUploadingImages(prev => [...prev, ...newUploadingImages]);

    // 清空文件输入
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 处理内容变化
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  };

  const effectiveToolbarMode = toolbarMode ?? (showToolbar ? 'full' : 'none');
  const showToolbarBar = effectiveToolbarMode === 'full';
  const showSymbolEntry = effectiveToolbarMode === 'full' || effectiveToolbarMode === 'symbolOnly';

  return (
    <Box sx={fillHeight ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' } : undefined}>
      {/* 完整工具栏 */}
      {showToolbarBar && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Code fontSize="small" />}
            onClick={handleInsertCode}
            disabled={disabled}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            插入代码
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MoreVert fontSize="small" />}
            onClick={() => setSymbolPanelOpen(!symbolPanelOpen)}
            disabled={disabled}
            color={symbolPanelOpen ? 'primary' : 'inherit'}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            插入符号
          </Button>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id={uploadInputId}
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileSelect}
            disabled={disabled || disableImageUpload}
          />
          <label htmlFor={uploadInputId}>
            <Button
              size="small"
              variant="outlined"
              component="span"
              startIcon={<ImageIcon fontSize="small" />}
              disabled={disabled || disableImageUpload}
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                ...(disableImageUpload ? {
                  color: 'text.disabled',
                  borderColor: 'divider',
                  cursor: 'not-allowed',
                } : {}),
              }}
            >
              上传图片
            </Button>
          </label>
        </Stack>
      )}

      {/* symbolOnly 模式：仅插入符号按钮（紧凑图标样式）；受控模式下由父组件渲染 */}
      {effectiveToolbarMode === 'symbolOnly' && !isSymbolPanelControlled && (
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          <Tooltip title="插入符号" placement="top">
            <Button
              size="small"
              variant="outlined"
              startIcon={<MoreVert fontSize="small" />}
              onClick={() => setSymbolPanelOpen(!symbolPanelOpen)}
              disabled={disabled}
              color={symbolPanelOpen ? 'primary' : 'inherit'}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'none',
                py: 0.5,
                px: 1,
                minWidth: 'auto',
              }}
            >
              符号
            </Button>
          </Tooltip>
        </Box>
      )}
      
      {/* 符号选择面板 - full 模式在上方，symbolOnly 模式在下方（与输入框同宽） */}
      {showSymbolEntry && effectiveToolbarMode !== 'symbolOnly' && (
        <Collapse in={symbolPanelOpen} timeout="auto">
          <Paper
            elevation={2}
            sx={{
              mb: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'grey.50'
            }}>
              <Tabs
                value={symbolCategoryTab}
                onChange={(e, newValue) => setSymbolCategoryTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 40,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'grey.50',
                  '& .MuiTab-root': {
                    minHeight: 40,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: (theme) => theme.palette.mode === 'dark' ? theme.palette.text.primary : 'inherit',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.main : 'inherit',
                  },
                }}
              >
                {symbolCategories.map((category, index) => (
                  <Tab key={index} label={category.name} />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
              <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                <TableBody>
                  {(() => {
                    const symbols = symbolCategories[symbolCategoryTab]?.symbols || [];
                    const columnsPerRow = 6; // 每行显示6个符号
                    const rows = [];
                    
                    for (let i = 0; i < symbols.length; i += columnsPerRow) {
                      const rowSymbols = symbols.slice(i, i + columnsPerRow);
                      rows.push(
                        <TableRow key={i}>
                          {rowSymbols.map((item, idx) => (
                            <TableCell
                              key={i + idx}
                              sx={{
                                p: 1,
                                textAlign: 'center',
                                cursor: 'pointer',
                                border: 1,
                                borderColor: 'divider',
                                minWidth: 50,
                                width: `${100 / columnsPerRow}%`,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  borderColor: 'primary.main',
                                },
                              }}
                              onClick={() => handleInsertSymbol(item.symbol)}
                            >
                              <Tooltip title={item.label} placement="top" arrow>
                                <Typography
                                  component="div"
                                  sx={{
                                    fontFamily: 'system-ui, -apple-system, "Segoe UI", "STIX Two Math", "Latin Modern Math", "Computer Modern", serif',
                                    fontWeight: 500,
                                    fontSize: '1.25rem',
                                    lineHeight: 1,
                                    // 针对组合符号使用更大的字号和更好的字体
                                    ...(item.symbol === '∋' || item.symbol === '∌' ? {
                                      fontSize: '1.5rem',
                                      fontFamily: 'system-ui, -apple-system, "Segoe UI", "STIX Two Math", "Latin Modern Math", "Computer Modern", serif',
                                      letterSpacing: '0',
                                    } : {}),
                                  }}
                                >
                                  {item.symbol}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          ))}
                          {/* 填充空单元格以保持表格对齐 */}
                          {Array.from({ length: columnsPerRow - rowSymbols.length }).map((_, idx) => (
                            <TableCell key={`empty-${idx}`} sx={{ border: 'none' }} />
                          ))}
                        </TableRow>
                      );
                    }
                    return rows;
                  })()}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Collapse>
      )}

      {/* 上传错误提示 */}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {/* 上传进度提示 */}
      {uploadingImages.length > 0 && (
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          {uploadingImages.map((image) => (
            <Chip
              key={image.id}
              label={
                image.uploading
                  ? `上传中 ${image.name}... ${image.progress || 0}%`
                  : image.error
                  ? `上传失败: ${image.name}`
                  : `已上传: ${image.name}`
              }
              color={image.uploading ? 'default' : image.error ? 'error' : 'success'}
              icon={image.uploading ? <CircularProgress size={16} /> : null}
              onDelete={() => {
                setUploadingImages(prev => prev.filter(img => img.id !== image.id));
              }}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}

      {/* 编辑/预览标签页 */}
      {showPreview ? (
        <Paper variant="outlined" sx={{ border: 1, borderColor: 'divider', overflow: 'hidden', ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, v) => setActiveTab(v)}
              sx={{ 
                minHeight: 48, 
                height: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
                '& .MuiTab-iconWrapper': {
                  fontSize: '1rem',
                },
              }}
            >
              <Tab 
                icon={<Edit fontSize="small" />} 
                iconPosition="start" 
                label="编辑"
              />
              <Tab 
                icon={<Visibility fontSize="small" />} 
                iconPosition="start" 
                label="预览"
              />
            </Tabs>
          </Box>
          <Box sx={fillHeight ? { pt: 1, px: 2, pb: 1, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : { p: 2 }}>
            {activeTab === 0 ? (
              <TextField
                inputRef={(el) => {
                  if (el) {
                    const textarea = el.querySelector('textarea');
                    if (textarea) {
                      contentInputRef.current = textarea;
                    } else {
                      contentInputRef.current = el;
                    }
                  }
                }}
                label={label}
                fullWidth
                multiline
                minRows={fillHeight ? undefined : adjustedMinRows}
                maxRows={fillHeight ? undefined : adjustedMaxRows}
                value={content}
                onChange={handleContentChange}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                variant="outlined"
                onFocus={(e) => {
                  const textarea = e.target.querySelector('textarea') || e.target;
                  contentInputRef.current = textarea;
                }}
                sx={fillHeight ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  '& .MuiInputBase-root': {
                    height: '100%',
                    minHeight: 0,
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-input': {
                    minHeight: '100% !important',
                    height: '100% !important',
                    boxSizing: 'border-box',
                  },
                } : undefined}
              />
            ) : (
              <Box
                sx={{
                  minHeight: fillHeight ? undefined : `${adjustedMinRows * 24}px`,
                  maxHeight: fillHeight ? undefined : `${adjustedMaxRows * 24}px`,
                  overflow: 'auto',
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
                }}
              >
                {content ? (
                  <MarkdownRenderer content={content} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    暂无内容，在编辑模式下输入内容后即可预览
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Box sx={fillHeight ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : undefined}>
        <Box sx={fillHeight ? { flex: 1, minHeight: 0, pt: 1, px: 2, pb: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : { pt: 1, px: 2, pb: 1 }}>
        <TextField
          inputRef={(el) => {
            if (el) {
              const textarea = el.querySelector('textarea');
              if (textarea) {
                contentInputRef.current = textarea;
              } else {
                contentInputRef.current = el;
              }
            }
          }}
          label={label}
          fullWidth
          multiline
          minRows={fillHeight ? undefined : adjustedMinRows}
          maxRows={fillHeight ? undefined : adjustedMaxRows}
          value={content}
          onChange={handleContentChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          variant="outlined"
          onFocus={(e) => {
            const textarea = e.target.querySelector('textarea') || e.target;
            contentInputRef.current = textarea;
          }}
          sx={fillHeight ? {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& .MuiInputBase-root': { height: '100%', minHeight: 0, alignItems: 'flex-start' },
            '& .MuiInputBase-input': { minHeight: '100% !important', height: '100% !important', boxSizing: 'border-box' },
          } : undefined}
        />
        {/* symbolOnly 模式：符号面板在输入框下方，与输入框同宽 */}
        {showSymbolEntry && effectiveToolbarMode === 'symbolOnly' && (
          <Box sx={{ flexShrink: 0, width: '100%', mt: 1 }}>
            <Collapse in={symbolPanelOpen} timeout="auto">
              <Paper
                elevation={2}
                sx={{
                  width: '100%',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                    <TableBody>
                      {(() => {
                        const columnsPerRow = 8;
                        const rows = [];
                        for (let i = 0; i < allSymbols.length; i += columnsPerRow) {
                          const rowSymbols = allSymbols.slice(i, i + columnsPerRow);
                          rows.push(
                            <TableRow key={i}>
                              {rowSymbols.map((item, idx) => (
                                <TableCell
                                  key={i + idx}
                                  sx={{
                                    p: 0.75,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    border: 1,
                                    borderColor: 'divider',
                                    minWidth: 40,
                                    width: `${100 / columnsPerRow}%`,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      bgcolor: 'primary.main',
                                      color: 'primary.contrastText',
                                      borderColor: 'primary.main',
                                    },
                                  }}
                                  onClick={() => handleInsertSymbol(item.symbol)}
                                >
                                  <Tooltip title={item.label} placement="top" arrow>
                                    <Typography
                                      component="div"
                                      sx={{
                                        fontFamily: 'system-ui, -apple-system, "Segoe UI", "STIX Two Math", "Latin Modern Math", "Computer Modern", serif',
                                        fontWeight: 500,
                                        fontSize: '1.125rem',
                                        lineHeight: 1,
                                        ...(item.symbol === '∋' || item.symbol === '∌' ? {
                                          fontSize: '1.35rem',
                                          fontFamily: 'system-ui, -apple-system, "Segoe UI", "STIX Two Math", "Latin Modern Math", "Computer Modern", serif',
                                          letterSpacing: '0',
                                        } : {}),
                                      }}
                                    >
                                      {item.symbol}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                              ))}
                              {Array.from({ length: columnsPerRow - rowSymbols.length }).map((_, idx) => (
                                <TableCell key={`empty-${idx}`} sx={{ border: 'none' }} />
                              ))}
                            </TableRow>
                          );
                        }
                        return rows;
                      })()}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </Collapse>
          </Box>
        )}
        </Box>
        </Box>
      )}
    </Box>
  );
}

export default MarkdownEditor;

